/**
 * @license
 * Copyright (c) 2004 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

export type DateDef = {
  year: number;
  month: number; // 1-based!
  date: number;
  hours: number;
  minutes: number;
};
// cache the converters
const formatterCache: Map<string, Intl.DateTimeFormat> = new Map();

export function getISODateOffset(date: DateDef, timeZone: string): number {
  // Get the timezone-adjusted representation of the date parameter interpreted as UTC
  const d = new Date(Date.UTC(date.year, date.month - 1, date.date, date.hours, date.minutes));
  const utcDateAtTimezone = _applyTimezoneToDate(d, timeZone);
  // get the offset of the target timezone (subject to a possible DTS change ajustment)
  const offset = _getOffset(date, utcDateAtTimezone);
  let adjustment = 0;
  d.setTime(d.getTime() - offset * 60000);

  // check whether the offset is correct
  if (!_compareDates(_applyTimezoneToDate(d, timeZone), date)) {
    // try to accommodate 'fall back'/move 1 hour west
    adjustment = -60;
    d.setTime(d.getTime() + 60 * 60000);
    if (!_compareDates(_applyTimezoneToDate(d, timeZone), date)) {
      // spring forward/move 1 hour east
      // This case should always produce a match unless we are hitting the missing hour during the 'spring forward'
      // JS Date() favors the DST in this case, so we will remain at the 'spring forward' offset even if _compareDates()
      // would have returned false
      adjustment = 60;
      d.setTime(d.getTime() - 120 * 60000);
    }
  }
  const result = offset + adjustment;
  return result;
}

function _applyTimezoneToDate(d: Date, timeZone: string): DateDef {
  const cnv = _getConverter(timeZone);
  const formattedUTC = cnv.format(d);
  const [localDate, localTime] = formattedUTC.split(',');
  const [month, date, year] = localDate.split('/');
  const [hours, minutes] = localTime.trim().split(':');
  return {
    year: parseInt(year),
    month: parseInt(month),
    date: parseInt(date),
    hours: parseInt(hours),
    minutes: parseInt(minutes)
  };
}

function _getOffset(original: DateDef, asUTC: DateDef): number {
  let originalMins = original.hours * 60 + original.minutes;
  let utcMinutes = asUTC.hours * 60 + asUTC.minutes;
  // any shift in year, month or day indicates the following day
  let delta = original.year - asUTC.year;
  if (delta == 0) {
    delta = original.month - asUTC.month;
    if (delta === 0) {
      delta = original.date - asUTC.date;
    }
  }
  if (delta > 0) {
    originalMins += 24 * 60;
  } else if (delta < 0) {
    utcMinutes += 24 * 60;
  }
  return utcMinutes - originalMins;
}

function _compareDates(date1: DateDef, date2: DateDef) {
  return (
    date1.year === date2.year &&
    date1.month === date2.month &&
    date1.hours === date2.hours &&
    date1.minutes === date2.minutes
  );
}

function _getConverter(timezone: string) {
  let formatter = formatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
      timeZone: timezone
    });
    formatterCache.set(timezone, formatter);
  }
  return formatter;
}
